/**
 * Appendix A scorecard display conventions, in exactly one place.
 *
 * These small details are what signal competence to cricket people, and incumbents
 * ship bugs in them — `187/10` for an all-out side, overs rendered as `12.66`. Every
 * surface formats through this module so a number cannot be right on the scorer's
 * phone and wrong on the public link.
 *
 * Pure string functions over `MatchState`. No cricket reasoning lives here: if a
 * value has to be *derived*, it belongs in the fold.
 */
import type { DismissalType } from "./events.ts";
import type { PlayerId } from "./ids.ts";
import type {
  BattingLine,
  BowlingLine,
  DismissalSummary,
  ExtrasBreakdown,
  FallOfWicket,
  InningsState,
  OverCount,
  OverStripEntry,
} from "./state.ts";

/** How a `PlayerId` becomes a display name. Supplied by the caller. */
export type NameLookup = (playerId: PlayerId) => string;

/**
 * `12.4` — twelve overs and four balls. **Never** `12.66`.
 *
 * The dot is a separator, not a decimal point, which is why `OverCount` is a pair
 * rather than a float.
 */
export const formatOvers = (overs: OverCount): string => `${overs.completed}.${overs.balls}`;

/** Two decimal places, or `-` when undefined. Never `0.00` for "no data", never NaN. */
export const formatRate = (rate: number | null): string =>
  rate === null ? "-" : rate.toFixed(2);

/**
 * `187 all out` for a completed innings, `143/4` otherwise.
 *
 * Cricket convention never writes `187/10`. The PRD calls this out by name as an
 * "absurd-but-telling detail bug" incumbents ship.
 */
export function formatScore(innings: Pick<InningsState, "runs" | "wickets" | "allOut">): string {
  return innings.allOut ? `${innings.runs} all out` : `${innings.runs}/${innings.wickets}`;
}

/**
 * `(b 4, lb 2, w 7, nb 1, p 5) 19`, or `-` when there are none.
 *
 * Broken out per type because byes, leg byes, wides and no-balls are distinct in the
 * scorecard and in bowler economy — folding any pair together is an incumbent bug.
 */
export function formatExtras(extras: ExtrasBreakdown): string {
  const parts: string[] = [];
  if (extras.bye > 0) parts.push(`b ${extras.bye}`);
  if (extras.legbye > 0) parts.push(`lb ${extras.legbye}`);
  if (extras.wide > 0) parts.push(`w ${extras.wide}`);
  if (extras.noball > 0) parts.push(`nb ${extras.noball}`);
  if (extras.penalty > 0) parts.push(`p ${extras.penalty}`);
  return parts.length === 0 ? "-" : `(${parts.join(", ")}) ${extras.total}`;
}

/**
 * `4-0-32-2 (8.00)` — overs, maidens, runs, wickets, then economy to 2dp.
 *
 * Complete overs print without a fractional part, exactly as Appendix A shows: `4`,
 * not `4.0`. A part-over still needs it, so `3.4-0-32-2`.
 */
export function formatBowling(line: BowlingLine): string {
  const overs = line.overs.balls === 0 ? String(line.overs.completed) : formatOvers(line.overs);
  return `${overs}-${line.maidens}-${line.runsConceded}-${line.wickets} (${formatRate(line.economy)})`;
}

/** The keeper is marked with † *inside* the fielder list, per Appendix A. */
const fielderName = (
  fielder: { playerId: PlayerId; isKeeper: boolean },
  name: NameLookup,
): string => `${fielder.isKeeper ? "†" : ""}${name(fielder.playerId)}`;

/**
 * `c Patel b Sharma`, `c †Khan b Sharma`, `c & b Sharma`, `lbw b Sharma`,
 * `run out (Verma/†Khan)`, `st †Khan b Sharma`, `b Sharma`, `hit wicket b Sharma`.
 */
export function formatDismissal(dismissal: DismissalSummary, name: NameLookup): string {
  const bowler = dismissal.bowlerId === null ? null : name(dismissal.bowlerId);
  const fielders = dismissal.fielders.map((f) => fielderName(f, name));
  const byBowler = bowler === null ? "" : ` b ${bowler}`;

  const notation: Record<DismissalType, () => string> = {
    bowled: () => `b ${bowler ?? "?"}`,
    // "c & b Sharma" when the bowler took his own catch — never "c Sharma b Sharma".
    caught_and_bowled: () => `c & b ${bowler ?? "?"}`,
    caught: () => `c ${fielders[0] ?? "?"}${byBowler}`,
    caught_behind: () => `c ${fielders[0] ?? "?"}${byBowler}`,
    lbw: () => `lbw${byBowler}`,
    stumped: () => `st ${fielders[0] ?? "?"}${byBowler}`,
    hit_wicket: () => `hit wicket${byBowler}`,
    run_out: () => `run out (${fielders.join("/") || "?"})`,
    mankad: () => `run out (${fielders.join("/") || "?"})`,
    obstructing_the_field: () => "obstructing the field",
    hit_ball_twice: () => "hit the ball twice",
    handled_ball: () => "handled the ball",
    timed_out: () => "timed out",
    retired_out: () => "retired out",
  };

  return notation[dismissal.type]();
}

/** `not out`, `retired hurt`, or the dismissal notation. */
export function formatBatterStatus(line: BattingLine, name: NameLookup): string {
  if (line.retired === "hurt") return "retired hurt";
  if (line.retired === "not_out") return "retired not out";
  if (line.retired === "out") return "retired out";
  if (line.dismissal === null) return "not out";
  return formatDismissal(line.dismissal, name);
}

/** `47 (32)` — runs then balls faced. */
export const formatBatting = (line: BattingLine): string => `${line.runs} (${line.balls})`;

/**
 * One over-strip chip: `•`, `4`, `wd`, `wd2`, `nb`, `nb4`, `b2`, `lb1`, `W`, `4W`.
 *
 * Lives here rather than in the scoring screen because the same chips are the public
 * page's over-by-over feed (#14). Two spellings of "a wide that went for two" is the
 * cosmetic half of the bug class this module exists to prevent — the scorer's phone
 * and the public link describing the same ball differently.
 *
 * Conventions: a dot ball is `•` and never `0`, because a column of zeroes is
 * unreadable at a glance. Extras carry their letter and *only* the runs run beyond
 * the one-run penalty, so `wd` is a plain wide and `wd2` is a wide they ran two off.
 */
export function formatBallChip(entry: OverStripEntry): string {
  const { extras, batter } = entry.runs;
  const wicket = entry.wicket ? "W" : "";

  // Runs beyond the automatic one-run penalty, so a plain wide reads `wd` not `wd1`.
  const beyondPenalty = (count: number): string => (count > 1 ? String(count - 1) : "");

  if (extras.wide > 0) return `wd${beyondPenalty(extras.wide)}${wicket}`;
  if (extras.noball > 0) {
    const offTheBat = batter + extras.bye + extras.legbye;
    return `nb${offTheBat > 0 ? String(offTheBat) : ""}${wicket}`;
  }
  if (extras.bye > 0) return `b${extras.bye}${wicket}`;
  if (extras.legbye > 0) return `lb${extras.legbye}${wicket}`;
  if (batter === 0) return wicket === "" ? "•" : wicket;
  return `${batter}${wicket}`;
}

/** `1-24 (Sharma, 3.2 ov)` — wicket number, team score, who went, when. */
export function formatFallOfWicket(fow: FallOfWicket, name: NameLookup): string {
  return `${fow.wicketNo}-${fow.runs} (${name(fow.playerOutId)}, ${formatOvers(fow.overs)} ov)`;
}

/** The striker carries `*`, which is how a reader knows who is on strike. */
export const strikerMark = (line: BattingLine): string => (line.isStriker ? "*" : "");
